import { PROFILE_BLOCK } from '../../../services/utilityFunctions';

/**
 * What an organization's required list must NOT demand from an INDIVIDUAL in
 * the Legal & Finances block, whatever it says (PureLiink lists all of these
 * for everyone): company name, SIREN and business type belong to the company
 * tab only, and the VAT type is a professional's identity — only a company is
 * held to it. Same rule as the portal v2 kit (BRAVON-6551, Freshdesk #2157).
 */
const NOT_REQUIRED_FROM_INDIVIDUAL = [
  'personalInfo.companyName',
  'personalInfo.SIREN',
  'personalInfo.businessType',
  'personalInfo.taxType',
  'personalInfo.taxCode',
];

const isFieldRequiredLabel = (
  requiredFieldsPerBlock,
  block,
  field,
  label,
  index,
  user,
  businessTypes = [],
) => {
  if (!requiredFieldsPerBlock || !block || !field) {
    return label;
  }

  if (
    user?.businessType &&
    user?.businessType !== 'individual' &&
    businessTypes &&
    (field === 'SIREN' || field === 'NIF')
  ) {
    if (field === 'SIREN') {
      return businessTypes?.find(b => b.id === user.businessType)
        ?.isSIRENRequired
        ? `${label}*`
        : label;
    }
    if (field === 'NIF') {
      return businessTypes?.find(b => b.id === user.businessType)?.isVATRequired
        ? `${label}*`
        : label;
    }
  }

  return requiredFieldsPerBlock[block].find(requiredField => {
    const fieldSplit = requiredField?.split('.');

    let fieldToCheck = field;
    if (typeof index !== 'number') {
      if (fieldSplit?.length === 1) {
        // eslint-disable-next-line prefer-destructuring
        fieldToCheck = fieldSplit[0];
      } else if (fieldSplit?.length > 1) {
        fieldToCheck = fieldSplit[fieldSplit.length - 1];
      }
    } else {
      fieldToCheck = fieldSplit[index];
    }
    return fieldToCheck === field;
  })
    ? `${label}*`
    : label;
};

const getValidationRules = (
  requiredFields,
  block,
  validationRules = {},
  extraValidation = {},
  index = null,
  businessType = 'individual',
  businessTypes = [],
) => {
  const newValidationRules = { ...validationRules };
  let requiredBlockFields = requiredFields[block]
    ? [...requiredFields[block]]
    : [];

  if (block === PROFILE_BLOCK.LEGAL_FINANCES && businessType === 'individual') {
    requiredBlockFields = requiredBlockFields.filter(
      field => !NOT_REQUIRED_FROM_INDIVIDUAL.includes(field),
    );
  }

  if (block === PROFILE_BLOCK.LEGAL_FINANCES && businessType !== 'individual') {
    const businessForUser =
      businessTypes?.find(b => b.id === businessType) || {};

    if (!businessForUser.isSIRENRequired || !businessForUser.isVATRequired) {
      const fieldsToRemove = [];
      if (!businessForUser.isSIRENRequired) {
        fieldsToRemove.push('personalInfo.SIREN');
      }
      if (!businessForUser.isVATRequired) {
        fieldsToRemove.push('personalInfo.NIF');
      }

      requiredBlockFields = requiredBlockFields.filter(
        field => !fieldsToRemove.includes(field),
      );
    }
    if (businessForUser.isSIRENRequired || businessForUser.isVATRequired) {
      if (businessForUser.isSIRENRequired) {
        requiredBlockFields.push('personalInfo.SIREN');
      }
      if (businessForUser.isVATRequired) {
        requiredBlockFields.push('personalInfo.NIF');
      }
    }
  }

  requiredBlockFields.forEach(field => {
    const fieldSplit = field?.split('.');
    let requiredField = field;
    if (typeof index !== 'number') {
      if (fieldSplit?.length === 1) {
        // eslint-disable-next-line prefer-destructuring
        requiredField = fieldSplit[0];
      } else if (fieldSplit?.length > 1) {
        requiredField = fieldSplit[fieldSplit.length - 1];
      }
    } else {
      requiredField = fieldSplit[index];
    }

    if (!extraValidation) {
      newValidationRules[requiredField] = { required: true };
    } else {
      newValidationRules[requiredField] = {
        required: true,
        ...extraValidation,
      };
    }
  });

  return newValidationRules;
};

const validateObject = obj => {
  // Check if it's an array
  if (Array.isArray(obj)) {
    // Validate based on array length
    return obj?.length === 0;
  }
  // Validate based on object keys
  return Object.keys(obj)?.length === 0;
};

const getErrors = (validationRules, state) => {
  const newErrors = {};
  Object.keys(validationRules || {}).forEach(field => {
    const isRequired = validationRules[field]?.required;

    if (isRequired && state[field] && typeof state[field] === 'object') {
      newErrors[field] = validateObject(state[field]);
    } else if (isRequired && (!state[field] || state[field]?.length === 0)) {
      newErrors[field] = true;
    }
  });

  return newErrors;
};

const checkIfHasErrors = errors =>
  Object.values(errors).find(err => err === true);

export {
  isFieldRequiredLabel,
  getValidationRules,
  getErrors,
  checkIfHasErrors,
};
